// Everything stays inside the guard: a re-run script must not redeclare top-level bindings.
if (!customElements.get("bloosh-fall-v2")) {
  // size=1: a product's sqrt(long side x sqrt(area)) as a share of the stage width.
  const BURST_SIZE = 0.13;
  const SPIN_DEG_PER_MS = 360 / 90000;
  const SPIN_RAMP_MS = 2500;
  const TABLE_DEG = 0.5;
  const TABLE_STEPS = 360 / TABLE_DEG;
  const SMOOTH_HALF = 24;
  const OVERLAP_SHARE = 0.01;
  const OVERLAP_STEP_DEG = 2;

  // Products orbit the box on an angle-dependent radius r(t): each frame the product's rectangle
  // (long side along the ray) must clear the box by `gap` and keep its rotated bounds `pad` inside
  // the stage. When no radius satisfies both at some angle, every product shrinks by the same
  // factor so the chosen proportions survive.
  const startBurst = (stage, settled, knobs, readout) => {
    const box = stage.querySelector("[data-burst-box]");
    const els = [...stage.querySelectorAll("[data-burst-item]")];
    // A denser burst interleaves two or three bands, so angular neighbours sit at different depths.
    const depths = els.length > 20 ? [0, 0.5, 1] : els.length > 10 ? [0.1, 0.9] : [0.3, 0.62];
    // Equal area alone would stretch a pencil to half the stage; weighting in the long side keeps
    // slim items readable without letting them dominate.
    const weight = (w, h) => Math.sqrt(Math.max(w, h) * Math.sqrt(w * h));
    const weights = els.map((el) => weight(Number(el.dataset.w), Number(el.dataset.h)));
    const median = [...weights].sort((a, b) => a - b)[weights.length >> 1];
    const items = els.map((el, i) => {
      // Source pixels to stage widths; true scale keeps one factor for all.
      const f = (BURST_SIZE * knobs.size) / (knobs.normalize ? weights[i] : median);
      const w = Number(el.dataset.w) * f;
      const h = Number(el.dataset.h) * f;
      return {
        el,
        img: el.firstElementChild,
        w,
        long: Math.max(w, h),
        short: Math.min(w, h),
        tall: h > w,
        start: -90 + (i * 360) / els.length,
        // Alternating depth within the free band avoids a clock-face ring.
        depth: depths[i % depths.length],
        // Band by band from the inside out once there are several.
        order: els.length > 10 ? Math.floor(i / depths.length) + (i % depths.length) * 2 : i,
      };
    });
    if (els.length > 10) items.forEach((item) => item.el.style.setProperty("--i", item.order));
    let g;

    const axisRange = (dir, centre, size, extent) => {
      const low = g.pad + extent - centre;
      const high = size - g.pad - extent - centre;
      if (low > high) return [Infinity, -Infinity];
      if (Math.abs(dir) < 1e-9)
        return low <= 0 && high >= 0 ? [-Infinity, Infinity] : [Infinity, -Infinity];
      return dir > 0 ? [low / dir, high / dir] : [high / dir, low / dir];
    };
    const radiusRange = (item, deg, scale) => {
      const l = (item.long * g.unit * scale) / 2;
      const w = (item.short * g.unit * scale) / 2;
      const rad = (deg * Math.PI) / 180;
      const c = Math.cos(rad);
      const s = Math.sin(rad);
      const ac = Math.abs(c);
      const as = Math.abs(s);
      // Separating axes of the product rect and the gap-inflated box; clearing any one suffices.
      const clearBox = Math.min(
        ac > 1e-9 ? (g.hx + l * ac + w * as) / ac : Infinity,
        as > 1e-9 ? (g.hy + l * as + w * ac) / as : Infinity,
        l + g.hx * ac + g.hy * as,
      );
      const x = axisRange(c, g.cx, g.width, l * ac + w * as);
      const y = axisRange(s, g.cy, g.height, l * as + w * ac);
      return [Math.max(clearBox, x[0], y[0]), Math.min(x[1], y[1]), c, s];
    };
    const largest = (ok, high) => {
      let low = 0;
      for (let step = 0; step < 14; step++) {
        const mid = (low + high) / 2;
        if (ok(mid)) low = mid;
        else high = mid;
      }
      return low;
    };
    const fits = (scale) =>
      items.every((item) => {
        for (let deg = 0; deg < 360; deg += 0.5) {
          const [low, high] = radiusRange(item, deg, scale);
          if (low > high) return false;
        }
        return true;
      });
    const measure = () => {
      const s = stage.getBoundingClientRect();
      const b = box.getBoundingClientRect();
      const gap = Math.max(6, s.width * 0.02);
      g = {
        width: s.width,
        height: s.height,
        cx: b.left - s.left + b.width / 2,
        cy: b.top - s.top + b.height / 2,
        hx: b.width / 2 + gap,
        hy: b.height / 2 + gap,
        // Leaves room for the burst easing's overshoot.
        pad: Math.max(8, s.width * 0.03),
        unit: s.width,
        scale: 1,
      };
      if (!fits(1)) g.scale = largest(fits, 1);
      // Neighbours may touch, never visibly stack.
      const allowed = s.width * OVERLAP_SHARE;
      if (overlap(g.scale) > allowed) g.scale = largest((scale) => overlap(scale) <= allowed, g.scale);
      tabulate(g.scale);
      stage.dataset.effectiveScale = g.scale.toFixed(2);
      if (readout)
        readout.textContent = `${readout.dataset.knobs} · eff ${stage.dataset.effectiveScale}`;
      items.forEach((item) => {
        item.img.style.width = `${item.w * g.unit * g.scale}px`;
      });
    };
    const tabulate = (scale) =>
      items.forEach((item) => {
        // The band edges have slope kinks at the stage corners; a circular moving average of the
        // target radius keeps the radial motion calm, and place() re-clamps to the exact band.
        const target = Array.from({ length: TABLE_STEPS }, (_, k) => {
          const [low, high] = radiusRange(item, k * TABLE_DEG, scale);
          return low + item.depth * (high - low);
        });
        item.table = target.map((_, k) => {
          let sum = 0;
          for (let j = -SMOOTH_HALF; j <= SMOOTH_HALF; j++)
            sum += target[(k + j + TABLE_STEPS) % TABLE_STEPS];
          return sum / (2 * SMOOTH_HALF + 1);
        });
      });
    const place = (item, deg, scale) => {
      const [low, high, c, s] = radiusRange(item, deg, scale);
      return [Math.min(high, Math.max(low, smoothRadius(item.table, deg))), c, s];
    };
    // Separating-axis penetration depth of two products, each with its long side along its ray.
    const penetration = (a, b) => {
      let depth = Infinity;
      for (const [ux, uy] of [[a.c, a.s], [-a.s, a.c], [b.c, b.s], [-b.s, b.c]]) {
        const extent = (p) => p.l * Math.abs(p.c * ux + p.s * uy) + p.w * Math.abs(p.c * uy - p.s * ux);
        depth = Math.min(depth, extent(a) + extent(b) - Math.abs((b.x - a.x) * ux + (b.y - a.y) * uy));
        if (depth <= 0) return 0;
      }
      return depth;
    };
    // The deepest overlap of any two products over a whole turn.
    const overlap = (scale) => {
      tabulate(scale);
      let worst = 0;
      for (let phase = 0; phase < 360; phase += OVERLAP_STEP_DEG) {
        const placed = items.map((item) => {
          const [r, c, s] = place(item, item.start + phase, scale);
          const half = (side) => (side * g.unit * scale) / 2;
          return { x: r * c, y: r * s, c, s, l: half(item.long), w: half(item.short) };
        });
        placed.forEach((a, i) => {
          for (let j = i + 1; j < placed.length; j++) worst = Math.max(worst, penetration(a, placed[j]));
        });
      }
      return worst;
    };
    const smoothRadius = (table, deg) => {
      const x = (((deg % 360) + 360) % 360) / TABLE_DEG;
      const k = Math.floor(x);
      return table[k] + (x - k) * (table[(k + 1) % TABLE_STEPS] - table[k]);
    };
    let phase = 0;
    let spinning = false;
    const render = () =>
      items.forEach((item) => {
        const deg = item.start + phase;
        const [r, c, s] = place(item, deg, g.scale);
        const turn = item.tall ? deg + 90 : deg;
        item.el.style.transform = `translate(${g.cx + r * c}px, ${g.cy + r * s}px)`;
        item.img.style.transform = `translate(-50%, -50%) rotate(${turn}deg)`;
        // Only the burst keyframes read these; writing them per spin frame costs a style recalc.
        if (!spinning) {
          item.el.style.setProperty("--r", `${turn}deg`);
          item.el.style.setProperty("--dx", `${r * c}px`);
          item.el.style.setProperty("--dy", `${r * s}px`);
        }
      });

    let visible = true;
    let running = false;
    let frame;
    let last = 0;
    let spun = 0;
    const loop = (now) => {
      // Clamped so a dropped or throttled frame cannot jump the orbit.
      const dt = Math.min(now - last, 64);
      last = now;
      spun += dt;
      phase += dt * SPIN_DEG_PER_MS * Math.min(1, spun / SPIN_RAMP_MS);
      render();
      frame = requestAnimationFrame(loop);
    };
    const update = () => {
      const go = spinning && visible && !document.hidden;
      if (go === running) return;
      running = go;
      if (go) {
        last = performance.now();
        frame = requestAnimationFrame(loop);
      } else cancelAnimationFrame(frame);
    };

    const events = new AbortController();
    const resizeObserver = new ResizeObserver(() => {
      measure();
      render();
    });
    resizeObserver.observe(stage);
    const intersectionObserver = new IntersectionObserver(([entry]) => {
      visible = entry.isIntersecting;
      update();
    });
    intersectionObserver.observe(stage);
    document.addEventListener("visibilitychange", update, { signal: events.signal });

    measure();
    render();
    if (!matchMedia("(prefers-reduced-motion: reduce)").matches) {
      const finish = () => {
        stage.classList.remove("is-playing");
        spinning = true;
        update();
      };
      // After a sign-up the page reloads; the burst already played on the visit before.
      if (settled) finish();
      else {
        const lastOut = items.reduce((a, b) => (b.order > a.order ? b : a)).img;
        lastOut.addEventListener("animationend", finish, { signal: events.signal, once: true });
        lastOut.addEventListener("animationcancel", finish, { signal: events.signal, once: true });
        stage.classList.add("is-playing");
      }
    }
    stage.classList.add("is-ready");

    return () => {
      events.abort();
      resizeObserver.disconnect();
      intersectionObserver.disconnect();
      cancelAnimationFrame(frame);
    };
  };

  const DRIFT_PX_PER_MS = 0.03;
  const EASE_MS = 450;
  const NATIVE_IDLE_MS = 150;
  const FLING_MIN = 0.3;
  const FLING_MAX = 3;

  // The strip scrolls through copies of its products and keeps its position inside one period, so
  // it never reaches an end. The drift, a mouse drag's fling and the stop under the mouse are one
  // velocity that eases toward the drift speed, or toward rest while hovered, keyboard-focused or
  // parked. A release slower than FLING_MIN parks the strip; only a fling unparks it, and the drift
  // then keeps the fling's direction. Touch, wheel and keys scroll natively; the loop keeps its
  // hands off until the strip settles, because writing scrollLeft mid-fling cancels the momentum.
  const loopProducts = (list) => {
    list.querySelectorAll("[data-loop-copy]").forEach((node) => node.remove());
    const originals = [...list.children];
    const events = new AbortController();
    const on = (type, handler) =>
      list.addEventListener(type, handler, { signal: events.signal, passive: type !== "pointerdown" });
    let period = 0;
    let x = 0;
    let v = DRIFT_PX_PER_MS;
    let direction = 1;
    let parked = false;
    let written = -1;
    let hovering = false;
    let touching = false;
    let drag = null;
    let nativeAt = -Infinity;
    let samples = [];

    const fill = () => {
      const copies = list.querySelectorAll("[data-loop-copy]");
      period = (copies[0]?.offsetLeft ?? 0) - originals[0].offsetLeft;
      const sets = copies.length / originals.length + 1;
      // Bounded by the viewport: an unstyled strip grows with its content and would never fill.
      const needed = period > 0 ? Math.ceil(1.5 + Math.min(list.clientWidth, innerWidth) / period) + 1 : 2;
      for (let set = sets; set < needed; set++)
        list.append(
          ...originals.map((row) => {
            const copy = row.cloneNode(true);
            copy.setAttribute("aria-hidden", "true");
            copy.dataset.loopCopy = "";
            copy.inert = true;
            return copy;
          }),
        );
      if (sets < needed) fill();
    };
    const wrap = () => {
      const shift = x < period / 2 ? period : x >= period * 1.5 ? -period : 0;
      x += shift;
      if (drag) drag.from += shift;
    };
    const write = () => {
      list.scrollLeft = x;
      written = list.scrollLeft;
    };
    const settle = (velocity) => {
      parked = Math.abs(velocity) < FLING_MIN;
      if (!parked) direction = Math.sign(velocity);
    };

    let last = 0;
    let frame;
    let running = false;
    const tick = (now) => {
      const dt = Math.min(now - last, 64);
      last = now;
      if (!touching && now - nativeAt >= NATIVE_IDLE_MS) {
        if (!drag) {
          const still = parked || hovering || list.matches(":focus-visible");
          const goal = still ? 0 : direction * DRIFT_PX_PER_MS;
          v += (goal - v) * (1 - Math.exp(-dt / EASE_MS));
          x += v * dt;
        }
        wrap();
        write();
      }
      frame = requestAnimationFrame(tick);
    };
    let visible = false;
    const update = () => {
      const go = visible && !document.hidden;
      if (go === running) return;
      running = go;
      if (go) {
        last = performance.now();
        frame = requestAnimationFrame(tick);
      } else cancelAnimationFrame(frame);
    };

    on("scroll", (event) => {
      // Sub-pixel rounding of our own writes stays under a pixel.
      if (drag || Math.abs(list.scrollLeft - written) < 1) return;
      x = list.scrollLeft;
      v = 0;
      nativeAt = event.timeStamp;
      samples = [...samples.filter((sample) => event.timeStamp - sample.t < 100), { t: event.timeStamp, x }];
    });
    on("touchstart", () => {
      touching = true;
      samples = [];
    });
    on("touchend", (event) => {
      if (event.touches.length) return;
      touching = false;
      const [first, recent] = [samples[0], samples.at(-1)];
      const fresh = recent && event.timeStamp - recent.t < 100 && recent.t > first.t;
      settle(fresh ? (recent.x - first.x) / (recent.t - first.t) : 0);
    });
    on("touchcancel", () => (touching = false));
    on("pointerdown", (event) => {
      if (event.pointerType !== "mouse" || event.button !== 0) return;
      event.preventDefault();
      list.setPointerCapture(event.pointerId);
      drag = { from: x, start: event.clientX, lastX: event.clientX, lastT: event.timeStamp, v: 0 };
      list.classList.add("is-dragging");
    });
    on("pointermove", (event) => {
      if (!drag || event.pointerType !== "mouse") return;
      const dt = event.timeStamp - drag.lastT;
      if (dt > 0) drag.v = 0.8 * ((drag.lastX - event.clientX) / dt) + 0.2 * drag.v;
      drag.lastX = event.clientX;
      drag.lastT = event.timeStamp;
      x = drag.from + drag.start - event.clientX;
    });
    const release = (event) => {
      if (!drag || event.pointerType !== "mouse") return;
      // A pointer held still before release throws nothing.
      const velocity = event.timeStamp - drag.lastT > 100 ? 0 : drag.v;
      v = Math.max(-FLING_MAX, Math.min(FLING_MAX, velocity));
      settle(velocity);
      drag = null;
      list.classList.remove("is-dragging");
    };
    on("pointerup", release);
    on("pointercancel", release);
    on("lostpointercapture", release);
    on("pointerenter", (event) => (hovering = event.pointerType === "mouse"));
    on("pointerleave", () => (hovering = false));
    on("dragstart", (event) => event.preventDefault());
    document.addEventListener("visibilitychange", update, { signal: events.signal });

    list.classList.add("is-looping");
    fill();
    x = period;
    write();
    const resizeObserver = new ResizeObserver(() => {
      const offset = x - period;
      fill();
      x = period + offset;
      wrap();
      write();
    });
    resizeObserver.observe(list);
    const intersectionObserver = new IntersectionObserver(([entry]) => {
      visible = entry.isIntersecting;
      update();
    });
    intersectionObserver.observe(list);

    return () => {
      events.abort();
      resizeObserver.disconnect();
      intersectionObserver.disconnect();
      cancelAnimationFrame(frame);
    };
  };

  // Owner tuning: ?box=0.35&size=1&normalize=1&n=20, or the same after #; burst=N aliases n.
  const burstKnobs = () => {
    const params = new URLSearchParams(location.search);
    const hash = location.hash.slice(1);
    const legacy = hash.match(/^burst(\d+)$/)?.[1];
    if (legacy) params.set("burst", legacy);
    else new URLSearchParams(hash).forEach((value, key) => params.set(key, value));
    let tuned = false;
    const knob = (keys, low, high, fallback) => {
      const raw = keys.map((key) => params.get(key)).find((value) => value != null && value !== "");
      const value = Number(raw);
      if (raw == null || !Number.isFinite(value)) return fallback;
      tuned = true;
      return Math.min(high, Math.max(low, value));
    };
    const knobs = {
      box: knob(["box"], 0.2, 0.6, 0.35),
      size: knob(["size"], 0.5, 4, 1),
      normalize: knob(["normalize"], 0, 1, 1) >= 0.5,
      n: Math.round(knob(["n", "burst"], 4, 30, 20)),
    };
    return { ...knobs, tuned };
  };

  customElements.define(
    "bloosh-fall-v2",
    class extends HTMLElement {
      connectedCallback() {
        this.events = new AbortController();
        const options = { signal: this.events.signal };
        const stage = this.querySelector("[data-burst]");
        const settled = !!this.querySelector(".bloosh-fall-waitlist__message--success");
        const knobs = burstKnobs();
        const extra = stage.querySelector("template[data-burst-extra]");
        const inline = stage.querySelectorAll("[data-burst-item]");
        [...inline].slice(knobs.n).forEach((item) => item.remove());
        const added = [...(extra?.content.children ?? [])]
          .slice(0, Math.max(0, knobs.n - inline.length))
          .map((item) => item.cloneNode(true));
        extra?.remove();
        const box = stage.querySelector("[data-burst-box]");
        box.before(...added);
        box.style.width = `${knobs.box * 100}%`;
        this.querySelector(".burst-knobs")?.remove();
        let readout;
        if (knobs.tuned) {
          readout = document.createElement("output");
          readout.className = "burst-knobs";
          const { box: b, size, normalize, n } = knobs;
          readout.dataset.knobs = `box ${b} · size ${size} · norm ${Number(normalize)} · n ${n}`;
          this.append(readout);
        }
        const start = () => {
          if (this.isConnected) this.stopBurst = startBurst(stage, settled, knobs, readout);
        };
        // Held until the added products can paint, so none pops in mid-burst.
        if (added.length) Promise.allSettled(added.map((item) => item.firstElementChild.decode())).then(start);
        else start();

        const marquee = this.querySelector(".bloosh-intro-brand-marquee");
        const original = marquee.querySelector(
          ".bloosh-intro-brand-marquee__content",
        );
        if (!marquee.querySelector("[data-v2-brand-clone]")) {
          const clone = original.cloneNode(true);
          clone.setAttribute("aria-hidden", "true");
          clone.setAttribute("data-v2-brand-clone", "");
          clone.inert = true;
          clone
            .querySelectorAll("[id], [data-shopify-editor-block]")
            .forEach((node) => {
              node.removeAttribute("id");
              node.removeAttribute("data-shopify-editor-block");
            });
          original.parentElement.append(clone);
        }
        const pause = marquee.querySelector("[data-brand-pause]");
        pause.addEventListener(
          "click",
          () => {
            const paused = marquee.classList.toggle("is-paused");
            pause.setAttribute("aria-pressed", String(paused));
            pause.setAttribute(
              "aria-label",
              paused
                ? "Wznów przewijanie marek"
                : "Wstrzymaj przewijanie marek",
            );
            pause.querySelector("span").textContent = paused ? "▶" : "Ⅱ";
          },
          options,
        );
        this.classList.add("is-enhanced");

        if (!matchMedia("(prefers-reduced-motion: reduce)").matches)
          this.stopLoop = loopProducts(this.querySelector(".products"));

        // A reload or back/forward restores the scroll with a smooth scroll that would replay it.
        const restored = ["reload", "back_forward"].includes(
          performance.getEntriesByType("navigation")[0]?.type,
        );
        if (!restored && !matchMedia("(prefers-reduced-motion: reduce)").matches) {
          const list = this.querySelector(".products");
          // Fires ahead of the fold so the rows have usually landed by the time they are seen.
          this.unpack = new IntersectionObserver(
            ([entry]) => {
              if (!entry.isIntersecting) return;
              this.unpack.disconnect();
              let order = 0;
              // Rows already on screen stay put: a row must never blink out once seen.
              for (const row of list.children)
                if (row.getBoundingClientRect().top >= innerHeight) {
                  row.style.setProperty("--i", order++);
                  row.classList.add("is-unpacking");
                }
            },
            {
              // From 990px Horizon scrolls .page-wrapper, not the window, and a nested scroller's
              // clip would cancel a viewport rootMargin.
              root:
                getComputedStyle(document.documentElement).overflowY === "hidden"
                  ? this.closest(".page-wrapper")
                  : null,
              rootMargin: "0px 0px 200px 0px",
            },
          );
          this.unpack.observe(list);
        }

        const countdown = this.querySelector("[data-release]");
        if (countdown) {
          const end = Number(countdown.dataset.release) * 1000;
          const label = countdown.querySelector("[data-countdown-label]");
          const time = countdown.querySelector("[data-countdown]");
          const pad = (n) => String(n).padStart(2, "0");
          const tick = () => {
            const left = Math.floor((end - Date.now()) / 1000);
            // Past the start the stock decides; a reload shows the buy buttons once it is set.
            if (left <= 0) {
              countdown.removeAttribute("role");
              countdown.textContent = countdown.dataset.fallback;
              clearInterval(this.countdownTimer);
              return;
            }
            const days = Math.floor(left / 86400);
            const clock = `${pad(Math.floor((left % 86400) / 3600))}:${pad(Math.floor((left % 3600) / 60))}:${pad(left % 60)}`;
            label.textContent = "Start sprzedaży za";
            time.textContent = days ? `${days} d ${clock}` : clock;
          };
          tick();
          this.countdownTimer = setInterval(tick, 1000);
        }

        const faq = this.querySelector(".faq");
        const answers = [...faq.querySelectorAll("details")];
        answers.forEach((answer) =>
          answer.addEventListener(
            "toggle",
            () => {
              if (answer.open)
                answers.forEach((other) => {
                  if (other !== answer) other.open = false;
                });
            },
            options,
          ),
        );
        // Reserve the tallest single answer so the closing invitation does not jump.
        const reserveAnswerSpace = () => {
          if (!this.isConnected) return;
          const clone = faq.cloneNode(true);
          clone.style.cssText = `position:absolute;visibility:hidden;pointer-events:none;left:0;top:0;width:${faq.getBoundingClientRect().width}px;min-height:0;`;
          clone.setAttribute("aria-hidden", "true");
          clone.inert = true;
          clone
            .querySelectorAll("[id]")
            .forEach((node) => node.removeAttribute("id"));
          faq.parentElement.append(clone);
          const items = [...clone.querySelectorAll("details")];
          items.forEach((item) => {
            item.open = false;
          });
          let height = clone.getBoundingClientRect().height;
          items.forEach((item) => {
            item.open = true;
            height = Math.max(height, clone.getBoundingClientRect().height);
            item.open = false;
          });
          clone.remove();
          faq.style.minHeight = `${Math.ceil(height)}px`;
        };
        document.fonts.ready.then(reserveAnswerSpace);
        let previousWidth = 0;
        this.resizeObserver = new ResizeObserver(([entry]) => {
          if (Math.abs(entry.contentRect.width - previousWidth) < 1) return;
          previousWidth = entry.contentRect.width;
          cancelAnimationFrame(this.resizeFrame);
          this.resizeFrame = requestAnimationFrame(reserveAnswerSpace);
        });
        this.resizeObserver.observe(faq);

        this.querySelectorAll('a[href="#fall-offer"]').forEach((link) =>
          link.addEventListener(
            "click",
            (event) => {
              event.preventDefault();
              const offer = this.querySelector("#fall-offer");
              offer.scrollIntoView({
                behavior: matchMedia("(prefers-reduced-motion: reduce)").matches
                  ? "instant"
                  : "smooth",
                block: "start",
              });
              const heading = offer.querySelector(".offer-heading");
              heading.tabIndex = -1;
              heading.focus({ preventScroll: true });
              history.replaceState(null, "", "#fall-offer");
            },
            options,
          ),
        );
      }

      disconnectedCallback() {
        this.events?.abort();
        this.stopBurst?.();
        clearInterval(this.countdownTimer);
        this.stopLoop?.();
        this.resizeObserver?.disconnect();
        this.unpack?.disconnect();
        cancelAnimationFrame(this.resizeFrame);
      }
    },
  );
}
